import { SuggestionOutput } from '@allocab/allocab-commons-shared-types';
import { useCallback, useEffect, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { Label } from './atomic/Label';
import { InfoPopover } from './InfoPopover';
import { Spinner } from './Spinner';
import useDebounce from '../hooks/useDebounce';
import { useFetchSuggestions } from '../hooks/useFetchSuggestions';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

export type SuggestionsInputProps = {
  className?: string;
  id?: string;
  label?: string;
  info?: string;
  errorMessage?: string;
  onChange: (_address: SuggestionOutput | undefined) => void;
};

export const SuggestionsInput = ({ className, label, id, errorMessage, info, onChange }: SuggestionsInputProps) => {
  const [address, setAddress] = useState<SuggestionOutput>();
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data, isFetching } = useFetchSuggestions(debouncedSearchQuery);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const openSuggestions = useCallback(() => {
    if (data) {
      setIsVisible(true);
    }
  }, [data]);

  const handleChangeQuery = (text: string) => {
    setSearchQuery(text);
    setAddress(undefined);
    onChange(undefined);
  };

  const handleSelect = (address: SuggestionOutput) => {
    setIsVisible(false);
    setAddress(address);
    onChange(address);
  };

  useEffect(() => {
    openSuggestions();
  }, [openSuggestions]);

  return (
    <div>
      {label ? (
        <div className="flex items-center mb-2 gap-2">
          <Label htmlFor={id} className={twJoin('mb-0', !!errorMessage && 'text-red-700 dark:text-red-500')}>
            {label}
          </Label>
          {info ? <InfoPopover text={info} /> : null}
        </div>
      ) : null}
      <div className="relative">
        <input
          id={id}
          placeholder="12 rue de rivoli, 75004 Paris"
          onChange={e => handleChangeQuery(e.currentTarget.value)}
          onFocus={openSuggestions}
          value={address?.formattedAddress ?? searchQuery}
          className={twJoin(
            'bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500',
            !!errorMessage &&
              'bg-red-50 border-red-500 text-red-900 dark:text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:bg-red-100 dark:border-red-400',
            className,
          )}
        />
        {isFetching ? (
          <div className="absolute top-2 right-2">
            <Spinner />
          </div>
        ) : null}
        <div
          ref={ref}
          className={twMerge(
            'z-10 absolute top-12 left-0 right-0 bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-700 dark:divide-gray-600 overflow-hidden',
            (!data || !isVisible) && 'hidden',
          )}
        >
          {data?.items.map(address => (
            <button
              type="button"
              key={address.formattedAddress}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
              onClick={() => handleSelect(address)}
            >
              {address.formattedAddress}
            </button>
          ))}
        </div>
      </div>
      {errorMessage ? <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errorMessage}</p> : null}
    </div>
  );
};
